import { remove, uniq } from "lodash-es";
import { type FC, type ReactElement, useEffect, useState } from "react";

import { Card, type GridCard } from "widgets";

import { getRawAttributeValueFromType, getSampleSummaryCellType } from "../../../app-utils/sample.js";
import { type Attribute, type AttributeValue, type Sample } from "../../../graphql.js";
import { type IIdentifiable, type ITableColumn } from "../../../redux/modules/table-data.js";
import InMemoryTableWithSelectionModel from "../../core/grid/InMemoryTableWithSelectionModel.js";

interface ISampleMultiValuesCardProps extends GridCard {
  sample: Sample;
  name: string;
  title?: string;
  values: AttributeValue[];
}

const SampleMultiValuesCard: FC<ISampleMultiValuesCardProps> = (props) => {
  const [groupNamesWithMultiValues, setGroupNamesWithMultiValues] = useState<string[]>([]);

  useEffect(() => {
    if (props.values && props.values.length > 0) {
      // Save a unique list of group names containing multiple values to the local state
      // We are only interested in column groups, therefore filter out everything without group name
      setGroupNamesWithMultiValues(uniq(props.values.map((v) => v.attribute.groupName)));
    }
  }, [props.values]);

  const sample: Sample = props.sample;
  if (!sample || groupNamesWithMultiValues.length === 0) {
    return null;
  }

  return (
    <Card title={props.title ? `Aligned ${props.title}` : "Sample Values"} id={"sample-parents-card"}>
      {
        // create one grid per group name, and provide it with the values for all multi-value attributes
        groupNamesWithMultiValues.map((g, i) => {
          return (
            <SampleMultiValuesGrid
              key={`mv-${i}`}
              groupName={g}
              values={props.values.filter((v) => v.attribute.groupName === g)}
            />
          );
        })
      }
    </Card>
  );
};

// the grid component, called individually for every group_name containing multi-value attributes

interface ISampleMultiValuesGridProps {
  groupName: string;
  values: AttributeValue[];
}

export function SampleMultiValuesGrid(props: ISampleMultiValuesGridProps): ReactElement {
  const [rowData, setRowData] = useState<IIdentifiable[]>([]);
  const [colDefs, setColDefs] = useState<Array<Partial<ITableColumn>>>([]);

  useEffect(() => {
    // record the max and min value index, defining the number of rows
    const maxValueIndex: number = Math.max(...props.values.map((v) => v.valueIndex));
    const minValueIndex: number = Math.min(...props.values.map((v) => v.valueIndex));

    // check if there is a head attribute defined, and keep its id if available (preferred first column)
    const headAttributeId = props.values.find((v) => v.attribute.isHead)?.attribute.id;

    // unique list of all attribute IDs associated with the values, defining the columns
    const attributeIds: number[] = uniq(props.values.map((v) => v.attribute.id));

    // place the head attribute at the beginning if available
    if (headAttributeId) {
      remove(attributeIds, (a) => a === headAttributeId);
      attributeIds.unshift(headAttributeId);
    }

    const colDefs: Array<Partial<ITableColumn>> = attributeIds?.map((a) => {
      const attributeValue: AttributeValue = props.values.find((b) => b.attribute.id === a);
      const attribute: Attribute = attributeValue?.attribute;
      const cellType: string = getSampleSummaryCellType(attributeValue);
      const myField = `${attribute?.id}_value`;

      return {
        name: attribute?.attributeName,
        field: myField,
        cellType: cellType,
        groupName: attribute?.groupName,
      };
    });
    setColDefs(colDefs);

    const rowGridData: IIdentifiable[] = [];
    if (minValueIndex !== null && maxValueIndex !== null) {
      for (let i = minValueIndex; i <= maxValueIndex; i++) {
        const row: any = { id: i };
        attributeIds.map((id: number) => {
          const currAttributeValue: AttributeValue = props.values.find(
            (a) => a.valueIndex === i && a.attribute.id === id,
          );
          row[`${id}_value`] = getRawAttributeValueFromType(
            currAttributeValue,
            currAttributeValue?.attribute.attributeType,
          );
        });
        rowGridData.push(row);
      }
    }
    setRowData(rowGridData);
  }, [props.values]);

  if (!props.values || props.values.length === 0) {
    return null;
  }

  return (
    <>
      <div
        style={{
          marginBottom: 20,
          boxShadow: "rgb(216 216 216) 0px 0px 0px 1px inset",
          borderRadius: "5px",
        }}
      >
        <InMemoryTableWithSelectionModel
          columns={colDefs}
          rowGridData={rowData}
          useSelectionModel={false}
          uniqueGridIdentifier={"multi-value-grid-" + props.groupName}
          noRowsMessage={"No values found."}
          gridProps={{ domLayout: "autoHeight" }}
        />
      </div>
    </>
  );
}

export { SampleMultiValuesCard };
